// Authentification OPAQUE (aPAKE) côté navigateur.
//
// Propriété centrale : **le mot de passe ne quitte jamais cette machine**. Le
// serveur n'en détient ni copie ni empreinte exploitable — il ne peut donc pas
// le divulguer, même sous contrainte. C'est ce qui rend l'aveuglement du
// serveur crédible jusqu'à la porte d'entrée.
//
// La lib (`@serenity-kit/opaque`) est elle-même du WebAssembly ; elle doit être
// « prête » avant tout appel. Client et serveur doivent utiliser la MÊME
// configuration : le backend n'impose ni `keyStretching` ni identifiants
// personnalisés, on reste donc sur les défauts de part et d'autre.

import * as opaque from '@serenity-kit/opaque';
import { api } from '../api/client';
import { rewrapMasterKey } from '../crypto/master-key';

/** Résultat d'un login réussi. */
export type Session = {
  sessionToken: string;
  expiresAt: string;
  /**
   * Clé dérivée du mot de passe par OPAQUE, connue du seul client.
   *
   * C'est la racine naturelle du chiffrement local (états MLS, blobs d'état
   * device). Elle ne doit **jamais** être persistée ni envoyée au serveur : elle
   * vit en mémoire, le temps de la session.
   */
  exportKey: string;
};

/** Login arrêté au palier 2FA : il manque le code TOTP. */
export type TwoFactorChallenge = {
  twoFactorRequired: true;
  pendingToken: string;
  /** Conservée pour être rendue au terme du second facteur. */
  exportKey: string;
};

export type LoginOutcome = Session | TwoFactorChallenge;

export function isTwoFactorChallenge(o: LoginOutcome): o is TwoFactorChallenge {
  return 'twoFactorRequired' in o;
}

/** Réponse du backend à `login/finish` : session émise, ou palier 2FA. */
type LoginFinishResponse =
  | { sessionToken: string; expiresAt: string }
  | { twoFactorRequired: true; pendingToken: string };

/**
 * Identité du compte enrôlé, telle que le jeton d'invitation la désigne. Le
 * `tenantSlug` est le sous-domaine du cabinet : c'est par là que l'invité se
 * connectera désormais.
 */
export type Enrolled = { tenantSlug: string; email: string };

/**
 * Pose le mot de passe d'un compte, sur présentation d'une invitation.
 *
 * Le compte visé est désigné **par le jeton**, pas par l'appelant : c'est ce qui
 * empêche quiconque de s'emparer d'un compte non encore enrôlé en devinant son
 * adresse. Le serveur renvoie le cabinet et l'e-mail correspondants — inutile de
 * les saisir.
 *
 * Le jeton est à usage unique : il est consommé dans la transaction qui pose le
 * mot de passe.
 */
export async function register(
  invitationToken: string,
  password: string,
): Promise<Enrolled> {
  await opaque.ready;

  const { clientRegistrationState, registrationRequest } =
    opaque.client.startRegistration({ password });

  const started = await api<{ registrationResponse: string } & Enrolled>(
    '/auth/register/start',
    { method: 'POST', body: { invitationToken, registrationRequest } },
  );

  const { registrationRecord } = opaque.client.finishRegistration({
    clientRegistrationState,
    registrationResponse: started.registrationResponse,
    password,
  });

  const finished = await api<{ ok: true } & Enrolled>('/auth/register/finish', {
    method: 'POST',
    body: { invitationToken, registrationRecord },
  });

  return { tenantSlug: finished.tenantSlug, email: finished.email };
}

/**
 * Ouvre une session. Renvoie soit la session, soit un palier 2FA à compléter
 * via {@link submitTwoFactor}.
 *
 * `deviceId` lie la session à un appareil : le backend exige ce lien pour
 * accéder à l'état MLS du device (`/device-state`).
 *
 * Le cabinet n'est plus transmis : le serveur le lit dans le nom d'hôte. En
 * développement (`localhost`, sans sous-domaine), `tenantSlug` prend le relais.
 */
export async function login(
  tenantSlug: string | null,
  email: string,
  password: string,
  deviceId?: string,
): Promise<LoginOutcome> {
  await opaque.ready;

  const { clientLoginState, startLoginRequest } = opaque.client.startLogin({
    password,
  });

  // Le cabinet n'est PLUS dans le corps : le serveur le déduit du sous-domaine.
  // `tenantSlug` ne sert qu'en développement (cf. api/client.ts).
  const { flowId, loginResponse } = await api<{
    flowId: string;
    loginResponse: string;
  }>('/auth/login/start', {
    method: 'POST',
    tenantSlug,
    body: { email, startLoginRequest },
  });

  const finish = opaque.client.finishLogin({
    clientLoginState,
    loginResponse,
    password,
  });

  // `undefined` = le serveur n'a pas prouvé qu'il connaissait ce mot de passe.
  // C'est ICI que le mauvais mot de passe est rejeté — côté client, sans que le
  // serveur ait rien appris. Cas couvert aussi : compte inexistant (le backend
  // répond une réponse leurre pour ne pas révéler l'existence du compte).
  if (!finish) {
    throw new Error('Identifiants invalides');
  }

  const outcome = await api<LoginFinishResponse>('/auth/login/finish', {
    method: 'POST',
    body: { flowId, finishLoginRequest: finish.finishLoginRequest, deviceId },
  });

  if ('twoFactorRequired' in outcome) {
    return { ...outcome, exportKey: finish.exportKey };
  }
  return { ...outcome, exportKey: finish.exportKey };
}

/** Complète le second facteur et obtient la session. */
export async function submitTwoFactor(
  challenge: TwoFactorChallenge,
  totpCode: string,
): Promise<Session> {
  const { sessionToken, expiresAt } = await api<{
    sessionToken: string;
    expiresAt: string;
  }>('/auth/login/2fa', {
    method: 'POST',
    body: { pendingToken: challenge.pendingToken, totpCode },
  });
  return { sessionToken, expiresAt, exportKey: challenge.exportKey };
}

/**
 * Change le mot de passe.
 *
 * Deux protocoles OPAQUE s'entrelacent : un **login** avec l'ancien mot de passe
 * (le serveur exige cette preuve — un jeton de session volé ne suffit pas à
 * s'emparer d'un compte) et un **enregistrement** du nouveau.
 *
 * Côté crypto, la seule chose qui bouge est l'enveloppe : K est ré-enveloppée
 * avec la KEK du nouveau mot de passe, mais reste **la même clé**. Rien n'est à
 * re-chiffrer, et les parts Shamir déjà déposées restent valides.
 *
 * Renvoie le nouvel `exportKey` : la session courante doit l'adopter, sinon elle
 * ne saurait plus déballer K.
 */
export async function changePassword(
  session: Session,
  masterKey: Uint8Array,
  oldPassword: string,
  newPassword: string,
): Promise<{ exportKey: string; otherSessionsRevoked: number }> {
  await opaque.ready;

  const { clientLoginState, startLoginRequest } = opaque.client.startLogin({
    password: oldPassword,
  });
  const { clientRegistrationState, registrationRequest } =
    opaque.client.startRegistration({ password: newPassword });

  const started = await api<{
    flowId: string;
    loginResponse: string;
    registrationResponse: string;
  }>('/auth/password/change/start', {
    method: 'POST',
    token: session.sessionToken,
    body: { startLoginRequest, registrationRequest },
  });

  const proof = opaque.client.finishLogin({
    clientLoginState,
    loginResponse: started.loginResponse,
    password: oldPassword,
  });
  // Ancien mot de passe faux : rejeté ici, sans que le serveur l'apprenne.
  if (!proof) {
    throw new Error('Ancien mot de passe invalide');
  }

  const { registrationRecord, exportKey } = opaque.client.finishRegistration({
    clientRegistrationState,
    registrationResponse: started.registrationResponse,
    password: newPassword,
  });

  const encryptedMasterKey = await rewrapMasterKey(masterKey, exportKey);

  const { otherSessionsRevoked } = await api<{
    ok: true;
    otherSessionsRevoked: number;
  }>('/auth/password/change/finish', {
    method: 'POST',
    token: session.sessionToken,
    body: {
      flowId: started.flowId,
      finishLoginRequest: proof.finishLoginRequest,
      registrationRecord,
      encryptedMasterKey,
    },
  });

  return { exportKey, otherSessionsRevoked };
}

/** Identité de la session courante, telle que le serveur la voit. */
export type Me = {
  userId: string;
  tenantId: string;
  deviceId: string | null;
  email: string | null;
  /** Le second facteur est-il en place ? C'est ce qui décide de l'écran 2FA. */
  twoFactorEnabled: boolean;
};

export function me(token: string): Promise<Me> {
  return api<Me>('/auth/me', { token });
}

export function logout(token: string): Promise<void> {
  return api<void>('/auth/logout', { method: 'POST', token });
}
